/**
 * When a screen breaks, the person stays in the app and the platform hears
 * about it.
 *
 * Until this existed the console had no error boundary at all: one screen
 * throwing while it drew took the whole application down to a blank page or
 * the router's bare error page, and the way back — reloading — could end at
 * the sign-in door. Nothing reached the platform, so the fault was invisible
 * to anybody but the person it happened to.
 *
 * Two parts. `ScreenBoundary` sits round each screen inside the shell, so a
 * screen that throws shows what went wrong with the menu still there, a way
 * to try again and a way out — and nobody is signed out. `installCrashReports`
 * hears the errors no boundary sees (event handlers, promises nobody awaited).
 * Both write a "crash:" line to the console's diagnostic log, which the
 * platform already keeps in its server log, attributed — so the next fault
 * names itself.
 */
import { Component, type ErrorInfo, type ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { voiceLogNow } from "../assistant/listen";
import { t } from "../i18n";

function where(): string {
  return typeof window === "undefined"
    ? ""
    : `${window.location.pathname}${window.location.search}${window.location.hash}`;
}

function firstFrames(stack: string | undefined, n = 4): string {
  return (stack ?? "")
    .split("\n")
    .map((l) => l.trim())
    .filter((l) => l && !l.startsWith("Error"))
    .slice(0, n)
    .join(" < ");
}

export function reportCrash(kind: string, err: unknown, extra = ""): void {
  const e = err instanceof Error ? err : new Error(String(err));
  try {
    voiceLogNow(
      `crash: ${kind} at ${where()} — ${e.name}: ${e.message}${extra ? ` | ${extra}` : ""} | ${firstFrames(e.stack)}`,
    );
  } catch {
    /* reporting must never be the second failure */
  }
}

let installed = false;
export function installCrashReports(): void {
  if (installed || typeof window === "undefined") return;
  installed = true;
  window.addEventListener("error", (ev) => reportCrash("uncaught", ev.error ?? ev.message));
  window.addEventListener("unhandledrejection", (ev) => reportCrash("unhandled promise", ev.reason));
}

interface State {
  error: Error | null;
}

/** Round one screen: a throw shows here, inside the app, and is reported. */
export class ScreenBoundary extends Component<{ children: ReactNode }, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    reportCrash("screen", error, firstFrames(info.componentStack ?? "", 5));
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;
    return (
      <div
        role="alert"
        data-testid="screen-crash"
        style={{ display: "grid", placeItems: "center", padding: 32, minHeight: "60vh" }}
      >
        <div style={{ maxWidth: 560, display: "grid", gap: 12, textAlign: "center" }}>
          <div className="eyebrow">{t("This screen hit a problem")}</div>
          <h1 style={{ fontSize: 22 }}>{t("Something on this screen could not be drawn")}</h1>
          <p className="muted">
            {t(
              "You are still signed in, and nothing you saved is lost. The fault has been sent to the platform's log so it can be fixed.",
            )}
          </p>
          <pre
            style={{
              textAlign: "start",
              whiteSpace: "pre-wrap",
              fontSize: 12.5,
              padding: 10,
              borderRadius: 6,
              background: "var(--panel2)",
              border: "1px solid var(--line)",
            }}
          >
            {error.name}: {error.message}
          </pre>
          <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
            <button className="btn" type="button" onClick={() => this.setState({ error: null })}>
              {t("Try again")}
            </button>
            <a href={import.meta.env.BASE_URL} className="btn">
              {t("Go to the dashboard")}
            </a>
          </div>
        </div>
      </div>
    );
  }
}

/** A fresh boundary for each screen: moving to another screen clears a crash. */
export function RouteBoundary({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  return <ScreenBoundary key={pathname}>{children}</ScreenBoundary>;
}
